import {useEffect, useRef, useState} from "react";
import type {Space} from "../../../../data/models/Space";
import {XyiconFeature, Permission, FieldDataType} from "../../../../generated/api/base";
import {TimeUtils} from "../../../../utils/TimeUtils";
import {FieldV5} from "../../details/FieldV5";
import {ClickToEditInputV5} from "../../input/clicktoedit/ClickToEditInputV5";
import {SelectInputV5} from "../../input/select/SelectInputV5";
import type {Type} from "../../../../data/models/Type";
import {ConfirmWindowV5} from "../../popup/ConfirmWindowV5";
import {useAppStore} from "../../../../StateManager";

interface ISpaceDefaultFieldsProps {
	readonly item: Space;
}

export const SpaceDefaultFieldsV5 = (props: ISpaceDefaultFieldsProps) => {
	const {item} = props;

	const [editingName, setEditingName] = useState<string>(item.name);

	const appState = useAppStore((state) => state.appState);
	const transport = appState.app.transport;
	let _lastValidName = useRef<string>(null);

	const [isUpdating, setIsUpdating] = useState<boolean>(false);

	const isNameValid = (name: string) => {
		return appState.actions.isSpaceNameValid(name, item);
	};

	const getErrorMessage = (name: string) => {
		return !name.trim() ? "Name cannot be empty!" : isNameValid(name) ? "" : "Value needs to be unique!";
	};

	const onChange = async (type: Type) => {
		const title = "Confirm Space Type Change";
		const message = "Once you change the space type, fields (and data) not assigned to the new space type will be removed. Do you wish to continue?";
		const config = {
			ok: "Change",
			cancel: "Cancel",
		};

		const confirmed = await ConfirmWindowV5.open(message, title, config);

		if (confirmed) {
			item.setSpaceType(type.id);
			await transport.updateSpaceType(item);
		}
	};

	const getSpacePermission = () => {
		return appState.actions.getModuleTypePermission(item.typeId, item.ownFeature, item.portfolioId);
	};

	const onBlurNameInput = async () => {
		if (isNameValid(editingName) && editingName !== _lastValidName.current) {
			setIsUpdating(true);
			await TimeUtils.waitUpdate(transport.appState.actions.updateFields([item], {name: editingName}), appState.app.notificationContainer);
			_lastValidName.current = editingName;
			setIsUpdating(false);
		} else {
			setEditingName(_lastValidName.current);
		}
	};

	const onInput = (name: string) => {
		setEditingName(name);
	};

	useEffect(() => {
		_lastValidName.current = item.name;
	}, [item]);

	const permission = getSpacePermission();

	return (
		<>
			<FieldV5
				label={appState.actions.getRefIdName(item.ownFeature)}
				className="space"
			>
				{item.refId}
			</FieldV5>
			<FieldV5
				className="TypeSelector space"
				label="Type"
			>
				<SelectInputV5
					options={appState.types[XyiconFeature.Space]}
					renderOptionWhenClosed={(item) => <span>{item.name}</span>}
					selected={item.type}
					onChange={onChange}
					disabled={permission < Permission.Update}
					isSameWidth={true}
				/>
			</FieldV5>
			<FieldV5
				label="Space Name"
				className="space stucked"
			>
				<ClickToEditInputV5
					value={editingName}
					onLiveChange={onInput}
					getErrorMessage={getErrorMessage}
					onBlur={onBlurNameInput}
					dataType={FieldDataType.SingleLineText}
					isUpdating={isUpdating}
					disabled={permission < Permission.Update}
				/>
			</FieldV5>
		</>
	);
};
